// src/private/src/pages/app/clients/components/dashboardUtils.js
// Utility functions and constants extracted from ClientDashboard.jsx
// to reduce main component parse overhead.

import { alpha } from '@mui/material/styles';
import { formatDateToYMD } from '@src/utils/dateUtils';

// ─── Table style constants ────────────────────────────────────────────────────

export const stickyCol = { position: 'sticky', left: 0, backgroundColor: 'background.paper', zIndex: 1 };
export const stickyHeadCol = { ...stickyCol, zIndex: 2 };
export const compactTableSx = {
  '& .MuiTableCell-root': { py: 0.75, px: 1.25, whiteSpace: 'nowrap' },
  '& .MuiTableHead-root .MuiTableCell-root': {
    fontWeight: 700,
    color: 'text.secondary',
    backgroundColor: 'background.default',
    borderBottomColor: 'divider',
  },
};
export const sectionSx = {
  borderRadius: 14,
  border: '1px solid',
  borderColor: 'divider',
  boxShadow: '0 18px 38px rgba(15, 23, 42, 0.08)',
  overflow: 'hidden',
  backgroundColor: 'transparent',
  '&:before': { display: 'none' },
};
export const sectionSummarySx = {
  px: 3,
  py: 1.5,
  bgcolor: 'background.default',
};
export const sectionDetailsSx = { p: 0, bgcolor: 'background.paper' };
export const stickyControlsSx = {
  position: 'sticky',
  top: 72,
  zIndex: 12,
  borderRadius: 14,
  boxShadow: '0 18px 32px rgba(15, 23, 42, 0.12)',
  border: '1px solid',
  borderColor: 'divider',
};

// ─── Constants ────────────────────────────────────────────────────────────────

export const dayRangeOptions = [7, 14, 21, 30];
export const momColumnCount = 29;
export const dodColumnCount = 29;
export const BACKEND_PAGE_SIZE = 50;
export const MAX_DRILLDOWN_FETCH_PAGES = 40;
export const BACKEND_PAGE_GUARD = 100;

export const CLIENT_DASHBOARD_LEAD_STATE_PREFIX = 'clientDashboardLeadState:v1:';
export const CLIENT_DASHBOARD_LEAD_CACHE_LIMIT = 25;

export const MAIN_LEAD_FIELDS = [
  'id', 'client_name', 'date_created', 'webhook_sent_datetime',
  'first_name', 'last_name', 'phone', 'email', 'source', 'territory',
  'zip_code', 'self_book_appointment_date_time', 'vertical', 'processed',
  'success_post', 'post_response',
].join(',');

export const DRILLDOWN_LEAD_FIELDS = [
  'id', 'date_created', 'first_name', 'last_name', 'phone', 'email',
  'source', 'territory', 'zip_code', 'self_book_appointment_date_time',
  'vertical', 'success_post', 'post_response', 'campaign_id', 'campaign',
  'campaign_name', 'channel', 'state', 'geo_state', 'geo_dma',
].join(',');

export const MULTI_SELECT_MENU_PROPS = {
  PaperProps: {
    style: {
      maxHeight: 48 * 4.5 + 8,
      width: 260,
    },
  },
};

// ─── Quick date presets ───────────────────────────────────────────────────────

export const DATE_PRESETS = [
  {
    label: 'Last 7 Days',
    getRange: () => {
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 6);
      return { startDate: start, endDate: end };
    },
  },
  {
    label: 'Last 14 Days',
    getRange: () => {
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 13);
      return { startDate: start, endDate: end };
    },
  },
  {
    label: 'Last 30 Days',
    getRange: () => {
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 29);
      return { startDate: start, endDate: end };
    },
  },
  {
    label: 'This Month',
    getRange: () => {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      return { startDate: start, endDate: now };
    },
  },
  {
    label: 'Last Month',
    getRange: () => {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end = new Date(now.getFullYear(), now.getMonth(), 0);
      return { startDate: start, endDate: end };
    },
  },
  {
    label: 'QTD',
    getRange: () => {
      const now = new Date();
      const quarterMonth = Math.floor(now.getMonth() / 3) * 3;
      const start = new Date(now.getFullYear(), quarterMonth, 1);
      return { startDate: start, endDate: now };
    },
  },
];

// ─── Metric table column definitions ──────────────────────────────────────────

export const ALL_METRIC_COLUMNS = [
  { key: 'spend', label: 'Spend', format: 'dollar', feeAware: true, default: true },
  { key: 'leads', label: 'Leads', format: 'number', default: true },
  { key: 'cpl', label: 'CPL', format: 'dollar', computed: (r) => safeDiv(r.spend, r.leads), default: true },
  { key: 'selfBook', label: 'Self Book', format: 'number' },
  { key: 'cpSelfBook', label: 'CP Self Book', format: 'dollar', computed: (r) => safeDiv(r.spend, r.selfBook) },
  { key: 'selfBookRate', label: 'Self Book Rate', format: 'percent', computed: (r) => safeDiv(r.selfBook, r.leads) },
  { key: 'accepted', label: 'Accepted', format: 'number' },
  { key: 'acceptedRate', label: 'Accepted Rate', format: 'percent', computed: (r) => safeDiv(r.accepted, r.leads) },
  { key: 'success', label: 'Success', format: 'number' },
  { key: 'successRate', label: 'Success Rate', format: 'percent', computed: (r) => safeDiv(r.success, r.leads) },
  { key: 'outOfArea', label: 'Out of Area', format: 'number' },
  { key: 'outOfAreaRate', label: 'OOA Rate', format: 'percent', computed: (r) => safeDiv(r.outOfArea, r.leads) },
  { key: 'booked', label: 'Booked', format: 'number', default: true, backend: true },
  { key: 'cpBooked', label: 'CP Booked', format: 'dollar', computed: (r) => safeDiv(r.spend, r.booked), default: true, backend: true },
  { key: 'setRate', label: 'Set Rate', format: 'percent', computed: (r) => safeDiv(r.booked, r.leads), default: true, backend: true },
  { key: 'issues', label: 'Issues', format: 'number', default: true, backend: true },
  { key: 'cpIssue', label: 'CP Issue', format: 'dollar', computed: (r) => safeDiv(r.spend, r.issues), backend: true },
  { key: 'issueRate', label: 'Issue Rate', format: 'percent', computed: (r) => safeDiv(r.issues, r.scheduled), default: true, backend: true },
  { key: 'leadToIssue', label: 'Lead To Issue', format: 'percent', computed: (r) => safeDiv(r.issues, r.leads), backend: true },
  { key: 'demos', label: 'Demos', format: 'number', default: true, backend: true },
  { key: 'cpDemo', label: 'CP Demo', format: 'dollar', computed: (r) => safeDiv(r.spend, r.demos), backend: true },
  { key: 'demoRate', label: 'Demo Rate', format: 'percent', computed: (r) => safeDiv(r.demos, r.issues), backend: true },
  { key: 'grossSold', label: 'Gross Sold', format: 'number', backend: true },
  { key: 'netSold', label: 'Net Sold', format: 'number', default: true, backend: true },
  { key: 'netCloseRate', label: 'Net Close Rate', format: 'percent', computed: (r) => safeDiv(r.netSold, r.demos), default: true, backend: true },
  { key: 'netAmount', label: 'Net Amount', format: 'dollar', default: true, backend: true },
  { key: 'costOfMarketing', label: 'Cost of Marketing', format: 'percent', computed: (r) => safeDiv(r.spend, r.netAmount), default: true, backend: true },
  { key: 'avgDeal', label: 'Avg Deal', format: 'dollar', computed: (r) => safeDiv(r.netAmount, r.netSold), default: true, backend: true },
  { key: 'revenuePerLead', label: 'Revenue per Lead', format: 'dollar', computed: (r) => safeDiv(r.netAmount, r.leads), backend: true },
];

export const DEFAULT_VISIBLE_COLUMNS = new Set(
  ALL_METRIC_COLUMNS.filter((c) => c.default).map((c) => c.key)
);

// Columns shown when a client has no backend data connected — only spend/lead metrics.
export const NO_BACKEND_VISIBLE_COLUMNS = new Set(
  ALL_METRIC_COLUMNS.filter((c) => c.default && !c.backend).map((c) => c.key)
);

// Keys that require backend data — used to detect if a client has any.
export const BACKEND_METRIC_KEYS = new Set(
  ALL_METRIC_COLUMNS.filter((c) => c.backend).map((c) => c.key)
);

// ─── Formatters ───────────────────────────────────────────────────────────────

export const axisCurrencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD',
  minimumFractionDigits: 0, maximumFractionDigits: 0,
});

export const tooltipCurrencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD',
  minimumFractionDigits: 2, maximumFractionDigits: 2,
});

export const formatAxisCurrency = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return axisCurrencyFormatter.format(value);
  }
  return value;
};

export const formatTooltipCurrency = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return tooltipCurrencyFormatter.format(value);
  }
  const numeric = Number(value);
  return Number.isFinite(numeric) ? tooltipCurrencyFormatter.format(numeric) : value;
};

// ─── Color / gradient helpers ─────────────────────────────────────────────────

export const safeAlpha = (color, opacity) => {
  if (!color) return color;
  try { return alpha(color, opacity); } catch { return color; }
};

export const CLIENT_BRAND_GRADIENTS = {
  'express-flooring': ['#0f172a', '#1d4ed8'],
};

export const gradientPalette = [
  ['#0b1538', '#2196f3'],
  ['#0f172a', '#0ea5e9'],
  ['#0f172a', '#22c55e'],
  ['#111827', '#7c3aed'],
  ['#0f172a', '#ef4444'],
];

export const isHexColor = (value = '') => /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test((value || '').trim());

export const toFullHex = (value = '') => {
  const hex = (value || '').replace('#', '').trim();
  if (hex.length === 3) {
    return `#${hex.split('').map((char) => char + char).join('')}`;
  }
  return `#${hex}`;
};

const channelShift = (channel, factor = 0) => {
  const clamped = Math.min(Math.max(channel, 0), 255);
  const delta = factor >= 0 ? (255 - clamped) * factor : clamped * factor;
  return Math.min(Math.max(Math.round(clamped + delta), 0), 255);
};

export const shadeHex = (hexColor, factor = 0) => {
  const fullHex = toFullHex(hexColor);
  const intVal = Number.parseInt(fullHex.slice(1), 16);
  if (Number.isNaN(intVal)) return fullHex;
  const r = (intVal >> 16) & 255;
  const g = (intVal >> 8) & 255;
  const b = intVal & 255;
  const toHex = (n) => n.toString(16).padStart(2, '0');
  return `#${toHex(channelShift(r, factor))}${toHex(channelShift(g, factor))}${toHex(channelShift(b, factor))}`;
};

export const normalizeSlug = (value = '') =>
  value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

export const hashString = (value = '') =>
  Array.from(value).reduce((acc, char) => acc + char.charCodeAt(0), 0);

export const buildGradient = (start, end) => `linear-gradient(120deg, ${start} 0%, ${end} 100%)`;

export const getClientGradient = (client) => {
  const brandColor = (client?.brand_color || '').trim();
  if (isHexColor(brandColor)) {
    const start = shadeHex(brandColor, -0.12);
    const end = shadeHex(brandColor, 0.18);
    return buildGradient(start, end);
  }
  const slug = normalizeSlug(client?.slug || client?.name || 'default');
  if (CLIENT_BRAND_GRADIENTS[slug]) {
    const [start, end] = CLIENT_BRAND_GRADIENTS[slug];
    return buildGradient(start, end);
  }
  const palette = gradientPalette;
  const hash = hashString(slug);
  const index = Number.isFinite(hash) ? hash % palette.length : 0;
  const [start, end] = palette[index];
  return buildGradient(start, end);
};

export const getInitials = (value = '') =>
  value.split(' ').filter(Boolean).slice(0, 2)
    .map((part) => part[0]?.toUpperCase?.() || '').join('');

// ─── Date helpers ─────────────────────────────────────────────────────────────

export const parseDateValue = (value) => {
  if (!value) return null;
  if (value instanceof Date && Number.isFinite(value.getTime())) return value;
  const str = String(value).trim();
  const ymdMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (ymdMatch) {
    const [, y, m, d] = ymdMatch.map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d, 12, 0, 0, 0));
    return Number.isNaN(dt.getTime()) ? null : dt;
  }
  const fallback = new Date(str);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
};

export const parseMonthRange = (value) => {
  if (!value) return null;
  const text = String(value).trim();
  const isoMatch = text.match(/^(\d{4})[-/](\d{1,2})(?:[-/]\d{1,2})?$/);
  if (isoMatch) {
    const year = Number(isoMatch[1]);
    const monthIndex = Number(isoMatch[2]) - 1;
    if (Number.isFinite(year) && monthIndex >= 0 && monthIndex < 12) {
      const start = new Date(year, monthIndex, 1, 0, 0, 0, 0);
      const end = new Date(year, monthIndex + 1, 0, 23, 59, 59, 999);
      return { startDate: start, endDate: end };
    }
  }
  const wordMatch = text.match(/^([A-Za-z]+)\s+(\d{4})$/);
  if (wordMatch) {
    const parsed = new Date(`${wordMatch[1]} 1, ${wordMatch[2]}`);
    if (!Number.isNaN(parsed.getTime())) {
      const start = new Date(parsed.getFullYear(), parsed.getMonth(), 1, 0, 0, 0, 0);
      const end = new Date(parsed.getFullYear(), parsed.getMonth() + 1, 0, 23, 59, 59, 999);
      return { startDate: start, endDate: end };
    }
  }
  const fallback = new Date(text);
  if (!Number.isNaN(fallback.getTime())) {
    const start = new Date(fallback.getFullYear(), fallback.getMonth(), 1, 0, 0, 0, 0);
    const end = new Date(fallback.getFullYear(), fallback.getMonth() + 1, 0, 23, 59, 59, 999);
    return { startDate: start, endDate: end };
  }
  return null;
};

export const parseYmdDate = (value, fallback) => {
  if (!value) return fallback;
  const parsed = new Date(`${value}T12:00:00`);
  return Number.isNaN(parsed.getTime()) ? fallback : parsed;
};

export const parseCalendarDate = (value) => {
  if (!value) return null;
  if (typeof value === 'string') {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (match) {
      const year = Number(match[1]);
      const monthIndex = Number(match[2]) - 1;
      const day = Number(match[3]);
      return new Date(year, monthIndex, day);
    }
  }
  const parsed = new Date(value);
  return Number.isFinite(parsed.getTime()) ? parsed : null;
};

export const isDateInRange = (value, start, end) => {
  if (!value || !start || !end) return false;
  const parsed = parseDateValue(value);
  if (!parsed) return false;
  const ts = parsed.getTime();
  return ts >= start.getTime() && ts <= end.getTime();
};

export const formatDayLabel = (value) => {
  if (!value) return 'Unknown';
  const normalizedDate = (() => {
    if (typeof value === 'string') {
      const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (match) {
        const [, year, month, day] = match.map(Number);
        const dateObj = new Date(year, month - 1, day);
        if (Number.isFinite(dateObj.getTime())) return dateObj;
      }
    }
    const fallback = new Date(value);
    return Number.isFinite(fallback.getTime()) ? fallback : null;
  })();
  if (!normalizedDate) return value;
  return `${normalizedDate.getMonth() + 1}/${normalizedDate.getDate()}`;
};

export const buildSingleDayRange = (value) => {
  const parsed = parseDateValue(value);
  if (!parsed) return null;
  const start = new Date(parsed);
  start.setHours(0, 0, 0, 0);
  const end = new Date(parsed);
  end.setHours(23, 59, 59, 999);
  return { startDate: start, endDate: end };
};

export const formatFileSize = (bytes) => {
  const value = Number(bytes);
  if (!Number.isFinite(value) || value <= 0) return 'Unknown size';
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  if (value < 1024 * 1024 * 1024) return `${(value / (1024 * 1024)).toFixed(1)} MB`;
  return `${(value / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

// ─── Pacing helpers ───────────────────────────────────────────────────────────

export const parsePacingPercent = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

export const getPacingSeverity = (value) => {
  const metric = parsePacingPercent(value);
  if (metric === null) return null;
  if (metric < 90) return 'under';
  if (metric < 100) return 'low';
  if (metric < 110) return 'medium';
  if (metric < 120) return 'elevated';
  return 'high';
};

export const pacingSeverityStyles = {
  under: (theme) => ({
    color: theme.palette.info.dark || theme.palette.info.main,
    backgroundColor: safeAlpha(theme.palette.info.light || theme.palette.info.main, 0.35),
    borderColor: safeAlpha(theme.palette.info.main, 0.45),
  }),
  low: (theme) => ({
    color: theme.palette.success.dark || theme.palette.success.main,
    backgroundColor: safeAlpha(theme.palette.success.light || theme.palette.success.main, 0.35),
    borderColor: safeAlpha(theme.palette.success.main, 0.5),
  }),
  medium: (theme) => ({
    color: theme.palette.warning.dark || theme.palette.warning.main,
    backgroundColor: safeAlpha(theme.palette.warning.light || theme.palette.warning.main, 0.35),
    borderColor: safeAlpha(theme.palette.warning.main, 0.45),
  }),
  elevated: (theme) => ({
    color: theme.palette.warning.dark || theme.palette.warning.main,
    backgroundColor: safeAlpha(theme.palette.warning.main, 0.25),
    borderColor: safeAlpha(theme.palette.warning.main, 0.6),
  }),
  high: (theme) => ({
    color: theme.palette.error.dark || theme.palette.error.main,
    backgroundColor: safeAlpha(theme.palette.error.light || theme.palette.error.main, 0.35),
    borderColor: safeAlpha(theme.palette.error.main, 0.45),
  }),
};

// ─── Math helpers ─────────────────────────────────────────────────────────────

export const safeDiv = (numerator, denominator) => {
  const a = Number(numerator);
  const b = Number(denominator);
  if (!Number.isFinite(a) || !Number.isFinite(b) || b === 0) return 0;
  return a / b;
};

// ─── Ingestion health helpers ─────────────────────────────────────────────────

export const INGESTION_STATUS_EMOJI = { ok: '\u2705', warning: '\u26a0\ufe0f', missing: '\u274c' };

export const formatRuntimeSeconds = (value) => {
  const seconds = Number(value);
  if (!Number.isFinite(seconds) || seconds < 0) return null;
  if (seconds < 1) return '<1s';
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds - minutes * 60);
  if (minutes && remainingSeconds) return `${minutes}m ${remainingSeconds}s`;
  if (minutes) return `${minutes}m`;
  return `${remainingSeconds}s`;
};

export const formatIsoInEST = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return null;
  return date.toLocaleString('en-US', { timeZone: 'America/New_York' });
};

export const dayKey = (value) => {
  const date = parseCalendarDate(value);
  if (!date || !Number.isFinite(date.getTime())) return null;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
};

export const isTaskFreshInLocalTimezone = (task) => {
  if (!task || task.status === 'missing') return false;
  if (task.name === 'backend_data') {
    if (!task.max_created_date) return false;
    const maxDay = dayKey(task.max_created_date);
    const nowDay = dayKey(new Date());
    if (!Number.isFinite(maxDay) || !Number.isFinite(nowDay)) return Boolean(task.fresh);
    return Math.floor((nowDay - maxDay) / (24 * 60 * 60 * 1000)) <= 1;
  }
  if (task.finished_at) {
    const finishedAt = new Date(task.finished_at);
    if (Number.isFinite(finishedAt.getTime())) {
      return Date.now() - finishedAt.getTime() <= 24 * 60 * 60 * 1000;
    }
  }
  return Boolean(task.fresh);
};

export const buildTaskStatusText = (task) => {
  const parts = [];
  parts.push((task?.status || 'missing').replace(/_/g, ' '));
  if (task && !isTaskFreshInLocalTimezone(task) && task.status !== 'missing') parts.push('stale');
  const runtime = formatRuntimeSeconds(task?.runtime_seconds);
  if (runtime) parts.push(runtime);
  if (task?.finished_at) {
    const label = formatIsoInEST(task.finished_at);
    if (label) parts.push(label);
  } else if (task?.status !== 'missing') {
    parts.push('no recent run');
  }
  if (task?.max_created_date) parts.push(`max date ${task.max_created_date}`);
  return parts.join(' \u2022 ');
};

// ─── Row display helpers ──────────────────────────────────────────────────────

export const getDisplayValue = (row = {}, keys = []) => {
  for (const key of keys) {
    const raw = row?.[key];
    if (raw === undefined || raw === null) continue;
    const str = String(raw).trim();
    if (!str) continue;
    if (/^[0-9]+$/.test(str)) continue;
    return str;
  }
  return null;
};

export const formatTerritory = (row = {}) => {
  return getDisplayValue(row, [
    'territory_name', 'territory', 'territory_label', 'territory_display',
    'client_zip_territory_name', 'client_zip_territory_label',
    'client_zip_territory_display',
  ]) || '-';
};

// ─── Session storage helpers ──────────────────────────────────────────────────

export const getDashboardLeadStorageKey = (clientSlug) =>
  `${CLIENT_DASHBOARD_LEAD_STATE_PREFIX}${clientSlug || 'default'}`;

export const readDashboardLeadState = ({ clientSlug, defaultStart, defaultEnd }) => {
  const defaultValue = {
    filters: { phone: '', email: '', id: '', zip_code: '', source: '', vertical: '', success_post: '' },
    page: 1, totalPages: 1, leads: [],
    startDate: defaultStart, endDate: defaultEnd, cacheEntries: [],
  };
  if (typeof window === 'undefined') return defaultValue;
  try {
    const raw = window.sessionStorage.getItem(getDashboardLeadStorageKey(clientSlug));
    if (!raw) return defaultValue;
    const parsed = JSON.parse(raw) || {};
    const startDate = parseYmdDate(parsed.startDate, defaultStart);
    const endDate = parseYmdDate(parsed.endDate, defaultEnd);
    return {
      filters: { ...defaultValue.filters, ...(parsed.filters || {}) },
      page: Number(parsed.page) > 0 ? Number(parsed.page) : 1,
      totalPages: Number(parsed.totalPages) > 0 ? Number(parsed.totalPages) : 1,
      leads: Array.isArray(parsed.leads) ? parsed.leads : [],
      startDate, endDate,
      cacheEntries: Array.isArray(parsed.cacheEntries) ? parsed.cacheEntries : [],
    };
  } catch {
    return defaultValue;
  }
};

// ─── Aggregation helpers ──────────────────────────────────────────────────────

export const normalizeSummaryDateKey = (value) => {
  if (value instanceof Date && Number.isFinite(value.getTime())) return formatDateToYMD(value);
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const ymdPrefix = trimmed.match(/^(\d{4}-\d{2}-\d{2})/);
  if (ymdPrefix?.[1]) return ymdPrefix[1];
  const parsed = new Date(trimmed);
  if (!Number.isFinite(parsed.getTime())) return null;
  return formatDateToYMD(parsed);
};

export const aggregateMetricRows = (
  rows,
  { labelKeys = [], fallbackLabel = 'Unknown', preserveKeys = [], feeByClient = new Map() } = {}
) => {
  if (!Array.isArray(rows) || !rows.length) return [];
  const normalizedLabelKeys = labelKeys.filter(Boolean);
  return Object.values(
    rows.reduce((acc, currentRow = {}) => {
      const labelValue = normalizedLabelKeys
        .map((key) => currentRow?.[key])
        .find((value) => typeof value === 'string' && value.trim().length > 0);
      const displayName = (labelValue || fallbackLabel).trim() || fallbackLabel;
      const bucketKey = displayName.toLowerCase();
      if (!acc[bucketKey]) {
        acc[bucketKey] = { uuid: currentRow.uuid || displayName, displayName };
        normalizedLabelKeys.forEach((key) => { if (currentRow[key] !== undefined) acc[bucketKey][key] = currentRow[key]; });
        preserveKeys.forEach((key) => { if (currentRow[key] !== undefined) acc[bucketKey][key] = currentRow[key]; });
      }
      const bucket = acc[bucketKey];
      const baseSpend = Number(currentRow?.total_spend_sum || 0);
      const clientId = Number(currentRow?.client_id);
      const feeRate = Number.isFinite(clientId) ? Number(feeByClient.get(clientId) || 0) : 0;
      bucket.spendWithFees = (bucket.spendWithFees || 0) + baseSpend * (1 + feeRate);
      if (!bucket.uuid && currentRow.uuid) bucket.uuid = currentRow.uuid;
      Object.entries(currentRow).forEach(([key, value]) => {
        if (key === 'uuid' || normalizedLabelKeys.includes(key)) return;
        const numericValue = Number(value);
        if (!Number.isNaN(numericValue) && Number.isFinite(numericValue)) {
          bucket[key] = (bucket[key] || 0) + numericValue;
        } else if (bucket[key] === undefined) {
          bucket[key] = value;
        }
      });
      return acc;
    }, {})
  );
};

export const aggregateSummaryRowsByDay = (rows, { feeByClient = new Map() } = {}) => {
  if (!Array.isArray(rows) || !rows.length) return [];
  const buckets = new Map();
  rows.forEach((row = {}) => {
    const dk = normalizeSummaryDateKey(row?.date);
    if (!dk) return;
    if (!buckets.has(dk)) buckets.set(dk, { date: dk });
    const bucket = buckets.get(dk);
    const baseSpend = Number(row?.total_spend_sum || 0);
    const clientId = Number(row?.client_id);
    const feeRate = Number.isFinite(clientId) ? Number(feeByClient.get(clientId) || 0) : 0;
    bucket.spendWithFees = (bucket.spendWithFees || 0) + baseSpend * (1 + feeRate);
    Object.entries(row).forEach(([key, value]) => {
      if (key === 'date' || key === 'id' || key === 'pk' || key === 'uuid' || key === 'ingestion_health' || key.endsWith('_id')) return;
      if (typeof value === 'boolean' || (typeof value === 'string' && value.trim() === '')) return;
      const numericValue = Number(value);
      if (Number.isFinite(numericValue)) bucket[key] = (bucket[key] || 0) + numericValue;
      else if (bucket[key] === undefined) bucket[key] = value;
    });
  });
  return Array.from(buckets.values()).sort((a, b) => a.date.localeCompare(b.date));
};

// ─── Spend with fees helper ───────────────────────────────────────────────────

export const getSpendWithFeesForRow = (row = {}, feeRate = 0) => {
  if (row.spendWithFees !== undefined && Number.isFinite(Number(row.spendWithFees))) {
    return Number(row.spendWithFees);
  }
  const base = Number(row.total_spend_sum || 0);
  return base * (1 + feeRate);
};

// ─── Backend totals builder ───────────────────────────────────────────────────

export const buildBackendTotalsFromSummary = (summary = {}) => {
  const safe = (val) => Number(val) || 0;
  return {
    booked: safe(summary.booked_sum ?? summary.booked),
    scheduled: safe(summary.scheduled_sum ?? summary.scheduled),
    issue: safe(summary.issues_sum ?? summary.issue),
    demo: safe(summary.demos_sum ?? summary.demo),
    gross: safe(summary.gross_sales_sum ?? summary.gross_sold_sum ?? summary.gross),
    net: safe(summary.net_sales_sum ?? summary.net_sold_sum ?? summary.net),
    gross_amount: safe(summary.gross_amount_sum ?? summary.gross_amount),
    net_amount: safe(summary.net_amount_sum ?? summary.net_amount),
  };
};

export const computeBackendCounts = (detail = {}) => {
  if (detail.backendTotals) {
    const t = detail.backendTotals;
    return {
      bookedCount: t.booked || 0, apptCount: t.scheduled || 0,
      issueCount: t.issue || 0, demoCount: t.demo || 0,
      soldCount: t.gross || 0, netCount: t.net || 0,
      grossSum: t.gross_amount || 0, netSum: t.net_amount || 0,
    };
  }
  const rangeStart = detail.dateRange?.startDate || null;
  const rangeEnd = detail.dateRange?.endDate || null;
  const rows = detail.backendFull || detail.backend || [];
  const getCreatedDate = (row = {}) =>
    row.created_date || row.created_at || row.date_created || row.created || row.prospect_date_created || row.prospect_created_at;
  let bookedCount = 0, soldCount = 0, apptCount = 0, issueCount = 0;
  let demoCount = 0, netCount = 0, grossSum = 0, netSum = 0;
  rows.forEach((row = {}) => {
    const soldOk = isDateInRange(row?.sold_date, rangeStart, rangeEnd);
    const apptOk = isDateInRange(row?.appointment_date, rangeStart, rangeEnd);
    const createdOk = isDateInRange(getCreatedDate(row), rangeStart, rangeEnd);
    if (createdOk && row?.appointment_date) bookedCount += 1;
    if (soldOk || apptOk) {
      if (row?.sold_date) soldCount += 1;
      if (row?.appointment_date) apptCount += 1;
      if (row?.issue) issueCount += 1;
      if (row?.demo) demoCount += 1;
      const netVal = Number(row?.net_amount);
      if (Number.isFinite(netVal) && netVal > 0) netCount += 1;
      const grossVal = Number(row?.gross_amount);
      if (Number.isFinite(grossVal)) grossSum += grossVal;
      if (Number.isFinite(netVal)) netSum += netVal;
    }
  });
  return { bookedCount, soldCount, apptCount, issueCount, demoCount, netCount, grossSum, netSum };
};

// ─── Section navigation anchors ───────────────────────────────────────────────

export const SECTION_NAV_ITEMS = [
  { id: 'section-verticals', label: 'Verticals' },
  { id: 'section-channels', label: 'Channels' },
  { id: 'section-campaigns', label: 'Campaigns', staffOnly: true },
  { id: 'section-territories', label: 'Territories' },
  { id: 'section-geo', label: 'Geo' },
  { id: 'section-budget-pacing', label: 'Budget' },
  { id: 'section-mom', label: 'MoM' },
  { id: 'section-dod', label: 'DoD' },
];
